import React, { useState, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { Pagination, PrimaryButton, Table, getOffsetPaginationMeta } from "widgets";
import { useSnackbarContext } from "widgets/SnackBar/SnackBar";
import Loader from "shared/Icons/Loader/Loader";
import { useUploadFileService, useFetchUploadedFileHistory, useFetchProcessedFileUploadService } from "store/backend/apiHooks";
import {
  UPLOADED_ONBOARDING_FILE_HISTORY_COLUMN,
  ONBOARDING_FILE_MODULE_ID,
  FILE_UPLOAD_STATUS,
  ONBOARDING_HISTORY_PAGE_SIZE_OPTIONS,
  DEFAULT_ONBOARDING_PAGE_NUMBER,
  DEFAULT_ONBOARDING_PAGE_SIZE,
  ZERO,
  ONE,
} from "constants/constants";

import styles from "./UploadOnboardingFile.module.scss";
import { formatDate } from "utils/logger";

const handleFileChange = (event, setSelectedFile) => {
  const file = event.target.files[ZERO];
  setSelectedFile(file);
};


const handleUpload = (
  selectedFile,
  scriptId,
  userId,
  openSnackbar,
  uploadFile,
  onUploadSuccess
) => {
  if (!selectedFile) {
    openSnackbar("Please select a file before uploading", "error");
    return;
  }
  uploadFile(
    { selectedFile, userId, scriptId },
    {
      onSuccess: onUploadSuccess,
      onError: (err) => {
        openSnackbar(err?.message || "File upload failed", "error");
      },
    }
  );
};

const handleOnClickDownloadProcessedFile = (fileId, openSnackbar, fetchProcessedFile) => {
  if (!fileId) {
    openSnackbar("No file available for download!", "error");
    return;
  }
  fetchProcessedFile(fileId, {
    onSuccess: (data) => {
      const link = document.createElement("a");
      link.href = data.signedUrl;
      link.click();
      openSnackbar("Processed File downloaded successfully!", "success");
    },
    onError: (err) => {
      openSnackbar(err.message || "Error fetching Processed File", "error");
    },
  });
};

const getUploadedBulkRegistrationHistoryTableRows = (
  uploadedFileHistory,
  isUploadedFileHistoryLoading,
  openSnackbar,
  fetchProcessedFile,
  isFetchProcessedFileLoading
) => {
  if (!Array.isArray(uploadedFileHistory)) {
    return [];
  }

  return uploadedFileHistory.map((historyRow) => ({
    name: historyRow.name,
    status: historyRow.status.name,
    totalRows: historyRow.result?.totalRows,
    errorRows: historyRow.result?.errorRows,
    uploadedAt: formatDate(historyRow.createdAt),
    downloadProcessedFile:
      historyRow.status.id === FILE_UPLOAD_STATUS.PROCESSED ? (
        <PrimaryButton
          onClick={() =>
            handleOnClickDownloadProcessedFile(
              historyRow.fileId,
              openSnackbar,
              fetchProcessedFile
            )
          }
          disabled={isUploadedFileHistoryLoading || isFetchProcessedFileLoading}
        >
          {isFetchProcessedFileLoading ? "Loading..." : "Download"}
        </PrimaryButton>
      ) : (
        <PrimaryButton disabled>Not Available Yet</PrimaryButton>
      ),
  }));
};



const UploadOnboardingFile = ({
  scriptId,
  uploadTitle,
  historyTitle,
  emptyStateLabel,
}) => {
  const { openSnackbar } = useSnackbarContext();
  const queryClient = useQueryClient();
  const [selectedFile, setSelectedFile] = useState(null);
  const [page, setPage] = useState(DEFAULT_ONBOARDING_PAGE_NUMBER);
  const [pageSize, setPageSize] = useState(DEFAULT_ONBOARDING_PAGE_SIZE);
  const fileInputRef = useRef();
  const { mutate: uploadFile, isPending: isUploadLoading } = useUploadFileService();
  const [loggedInUserId] = useState(localStorage.getItem("loggedInUserId"));

  const offset = (page - ONE) * pageSize;

  const {
    data: uploadedFileHistory,
    isLoading: isUploadedFileHistoryLoading,
    isFetching: isUploadedFileHistoryFetching,
  } = useFetchUploadedFileHistory({
    moduleId: ONBOARDING_FILE_MODULE_ID,
    offset,
    limit: pageSize,
  });

  const { mutate: fetchProcessedFile, isPending: isFetchProcessedFileLoading } = useFetchProcessedFileUploadService();

  const historyRows = Array.isArray(uploadedFileHistory?.data)
    ? uploadedFileHistory.data
    : [];
  const filteredHistoryRows = historyRows.filter(
    (historyRow) => historyRow.script?.id === scriptId
  );
  const uploadedBulkRegistrationHistory = getUploadedBulkRegistrationHistoryTableRows(
    filteredHistoryRows,
    isUploadedFileHistoryLoading,
    openSnackbar,
    fetchProcessedFile,
    isFetchProcessedFileLoading
  );
  const historyCount = uploadedBulkRegistrationHistory.length;
  const paginationMeta = getOffsetPaginationMeta({
    itemCount: historyRows.length,
    page,
    pageSize,
  });
  const showPagination =
    !isUploadedFileHistoryLoading &&
    (historyCount > ZERO ||
      paginationMeta.hasPreviousPage ||
      paginationMeta.hasNextPage);
  const showExactHistoryCount =
    page === DEFAULT_ONBOARDING_PAGE_NUMBER &&
    !paginationMeta.hasNextPage &&
    historyCount > ZERO;

  const handleUploadSuccess = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setPage(DEFAULT_ONBOARDING_PAGE_NUMBER);
    queryClient.invalidateQueries({ queryKey: ["uploadedFileHistory"] });
    openSnackbar("File Uploaded Successfully!", "success");
  };

  const handlePageChange = (nextPage) => {
    setPage(nextPage);
  };

  const handlePageSizeChange = (nextPageSize) => {
    setPageSize(nextPageSize);
    setPage(DEFAULT_ONBOARDING_PAGE_NUMBER);
  };

  return (
    <>
      <div className={styles.uploadPanel}>
        <h2 className={styles.sectionTitle}>{uploadTitle}</h2>
        <div className={styles.uploadContent}>
          <div className={styles.dashedBox}>
            <p className={styles.uploadHint}>
              {selectedFile?.name || "Upload your file here."}
            </p>
            <input
              type="file"
              onChange={(event) => handleFileChange(event, setSelectedFile)}
              ref={fileInputRef}
              className={styles.fileInput}
            />
          </div>
          <div className={styles.uploadActions}>
            <PrimaryButton
              disabled={!selectedFile || isUploadLoading}
              onClick={() =>
                handleUpload(
                  selectedFile,
                  scriptId,
                  loggedInUserId,
                  openSnackbar,
                  uploadFile,
                  handleUploadSuccess
                )
              }
            >
              {isUploadLoading ? <Loader /> : "Upload"}
            </PrimaryButton>
          </div>
        </div>
      </div>

      <section
        className={styles.resultsPanel}
        aria-labelledby="onboarding-history-heading"
      >
        <header className={styles.resultsPanelHeader}>
          <div className={styles.resultsTitleRow}>
            <h2 id="onboarding-history-heading" className={styles.resultsHeading}>
              {historyTitle}
            </h2>
            {isUploadedFileHistoryLoading ? (
              <span className={styles.resultsHeaderMeta}>Loading…</span>
            ) : showExactHistoryCount ? (
              <span className={styles.resultsCountBadge} aria-live="polite">
                {historyCount === ONE ? "1 file" : `${historyCount} files`}
              </span>
            ) : paginationMeta.hasNextPage || paginationMeta.hasPreviousPage ? (
              <span className={styles.resultsHeaderMeta}>
                Page {paginationMeta.page}
              </span>
            ) : null}
          </div>
        </header>
        <div className={styles.resultsBody}>
          {isUploadedFileHistoryLoading ? (
            <div className={styles.resultsStateWrap}>
              <Loader />
            </div>
          ) : historyCount > ZERO ? (
            <Table
              columns={UPLOADED_ONBOARDING_FILE_HISTORY_COLUMN}
              rows={uploadedBulkRegistrationHistory}
              maxHeight="32rem"
            />
          ) : paginationMeta.hasPreviousPage || paginationMeta.hasNextPage ? (
            <p className={styles.resultsEmpty}>No files on this page.</p>
          ) : (
            <p className={styles.resultsEmpty}>
              No {emptyStateLabel} files uploaded yet. Choose a file above and click{" "}
              <strong>Upload</strong> to see it listed here.
            </p>
          )}
        </div>
        {showPagination && (
          <footer className={styles.resultsFooter}>
            <Pagination
              page={paginationMeta.page}
              totalPages={paginationMeta.totalPages}
              pageSize={paginationMeta.pageSize}
              pageSizeOptions={ONBOARDING_HISTORY_PAGE_SIZE_OPTIONS}
              summaryLabel={`Page ${paginationMeta.page}`}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              disabled={isUploadedFileHistoryFetching}
            />
          </footer>
        )}
      </section>
    </>
  );
};

export default UploadOnboardingFile;
